import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { AlertCircle, CheckCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { format, differenceInDays } from "date-fns";

export function ComplianceTracker() {
  const [complianceItems, setComplianceItems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, completed: 0, overdue: 0, dueSoon: 0 });

  useEffect(() => {
    fetchComplianceItems();
  }, []);

  const fetchComplianceItems = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("compliance_items")
      .select("*")
      .order("next_review_date", { ascending: true });

    if (error) {
      toast.error("Failed to load compliance items");
      setIsLoading(false);
      return;
    }

    // Calculate stats
    const total = data?.length || 0;
    const completed = data?.filter((item) => item.status === "completed").length || 0;
    const today = new Date();
    const overdue = data?.filter((item) => {
      const reviewDate = new Date(item.next_review_date);
      return reviewDate < today && item.status !== "completed";
    }).length || 0;
    const dueSoon = data?.filter((item) => {
      const reviewDate = new Date(item.next_review_date);
      const daysUntil = differenceInDays(reviewDate, today);
      return daysUntil > 0 && daysUntil <= 30 && item.status !== "completed";
    }).length || 0;

    setStats({ total, completed, overdue, dueSoon });
    setComplianceItems(data || []);
    setIsLoading(false);
  };

  const getStatusBadge = (item: any) => {
    const reviewDate = new Date(item.next_review_date);
    const today = new Date();
    const daysUntil = differenceInDays(reviewDate, today);

    if (item.status === "completed") {
      return (
        <Badge className="gap-1 bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3" />
          Completed
        </Badge>
      );
    }

    if (daysUntil < 0) {
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertCircle className="h-3 w-3" />
          Overdue by {Math.abs(daysUntil)} days
        </Badge>
      );
    }

    if (daysUntil <= 30) {
      return (
        <Badge className="gap-1 bg-orange-100 text-orange-800">
          <Clock className="h-3 w-3" />
          Due in {daysUntil} days
        </Badge>
      );
    }

    return (
      <Badge variant="secondary" className="gap-1">
        <Clock className="h-3 w-3" />
        Due in {daysUntil} days
        </Badge>
    );
  };

  const getDepartmentBadge = (department: string) => {
    const colors: Record<string, string> = {
      legal_compliance: "bg-red-100 text-red-800",
      finance: "bg-green-100 text-green-800",
      marketing: "bg-purple-100 text-purple-800",
      operations: "bg-blue-100 text-blue-800",
      hr: "bg-yellow-100 text-yellow-800",
      clinical: "bg-pink-100 text-pink-800",
    };
    return (
      <Badge className={colors[department] || "bg-gray-100 text-gray-800"}>
        {department.replace("_", " ")}
      </Badge>
    );
  };

  const completionPercentage = stats.total > 0 ? (stats.completed / stats.total) * 100 : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Items</CardDescription>
            <CardTitle className="text-3xl">{stats.total}</CardTitle>
          </CardHeader>
          <CardContent>
            <Progress value={100} className="h-2" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Completed</CardDescription>
            <CardTitle className="text-3xl text-green-600">{stats.completed}</CardTitle>
          </CardHeader>
          <CardContent>
            <Progress value={completionPercentage} className="h-2" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Overdue</CardDescription>
            <CardTitle className="text-3xl text-red-600">{stats.overdue}</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.overdue > 0 && (
              <div className="flex items-center gap-2 text-sm text-red-600">
                <AlertCircle className="h-4 w-4" />
                Requires immediate attention
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Due Soon (30 days)</CardDescription>
            <CardTitle className="text-3xl text-orange-600">{stats.dueSoon}</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.dueSoon > 0 && (
              <div className="flex items-center gap-2 text-sm text-orange-600">
                <Clock className="h-4 w-4" />
                Plan review soon
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Compliance Review Schedule</CardTitle>
          <CardDescription>
            Track annual reviews and compliance requirements
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {isLoading ? (
              <div className="text-center py-8">Loading compliance items...</div>
            ) : complianceItems.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No compliance items tracked yet.
              </div>
            ) : (
              complianceItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold">{item.title}</h4>
                      {getDepartmentBadge(item.department)}
                    </div>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>Review every {item.review_frequency_days} days</span>
                      {item.last_review_date && (
                        <span>
                          Last reviewed: {format(new Date(item.last_review_date), "dd MMM yyyy")}
                        </span>
                      )}
                      <span>
                        Next review: {format(new Date(item.next_review_date), "dd MMM yyyy")}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {getStatusBadge(item)}
                    <Button size="sm" variant="outline">
                      Mark Complete
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
